import React, { useRef, useState } from 'react'
import breakpoints from '../../constants/breakpoints'
import theme from '../../theme'

import i18n from '../../i18n'
import { rgba } from 'polished'
import styled from 'styled-components'
import { VideoType as Props } from '../../types'
import VideoJS from './VideoJS'

declare global {
  interface Window {
    player: any
  }
}

interface Configuration {
  controls?: boolean
  language?: any
  poster?: string
  preload?: string
  sources: Sources[]
  textTrackSettings: any
  userActions: any
  responsive: boolean
  fluid: boolean
}

interface Sources {
  type: string
  src: string
}

const Video: React.FC<Props> = ({ src, title, poster, subtitles, soundSrc, videoSize }) => {
  const [isOpen, setIsOpen] = useState(false)
  const playerRef = useRef<any>(null)
  const [duration, setDuration] = useState(0)

  duration;
  videoSize;
  Meta;

  const currentYear = new Date().getFullYear()

  const handleAccordion = () => {
    setIsOpen(!isOpen)
  }

  const videoJsOptions: Configuration = {
    controls: true,
    language: 'nl',
    preload: 'auto',
    responsive: true,
    fluid: true,
    poster,
    sources: [
      {
        src,
        type: 'video/mp4',
      },
    ],
    textTrackSettings: false,
    userActions: {
      hotkeys: false,
    },
  }

  const handlePlayerReady = (player: any) => {
    playerRef.current = player
  }

  const formattedDuration = `${src.split('.').pop()}, ${duration} minuten, ${videoSize}`;

  return (
    <>
      {title ? <Header>{title}</Header> : <Header>{i18n.t('video.watch')}</Header>}
      <StyledVideo tabIndex={0} aria-label={i18n.t('video.watchTheVideo')}>
        <VideoJS
          subtitles={subtitles}
          setDuration={setDuration}
          options={videoJsOptions}
          onReady={handlePlayerReady}
          data-matomo-title={title}
        />
      </StyledVideo>
      <DownloadHeader>
        <DownloadVideo
          tabIndex={0}
          onClick={handleAccordion}
          $isOpen={isOpen}
          aria-expanded={isOpen}
          aria-controls={'video-' + src}
        >
          {i18n.t('video.download')}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12.8 7.8" id="arrow_big">
            <path
              fill="currentColor"
              d="M6.4 7.8c-.3 0-.5-.1-.7-.3L.3 1.7C-.1 1.3-.1.7.3.3c.4-.4 1-.4 1.4 0l4.7 5 4.7-5c.4-.4 1-.4 1.4 0 .4.4.4 1 0 1.4L7.1 7.5c-.1.2-.4.3-.7.3z"
            ></path>
          </svg>
        </DownloadVideo>
      </DownloadHeader>
      <Downloads $isOpen={isOpen} id={'video-' + src}>
        {src && (
          <List>
            <a href={src} target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 22 15" id="video">
                <g fill="currentColor">
                  <path d="M8.5 11.6c-.1 0-.2 0-.2-.1-.2 0-.3-.2-.3-.4V4.5c0-.2.1-.4.3-.4.1-.1.3-.1.5 0l5 3.3c.1.1.2.3.2.4 0 .2-.1.3-.2.4l-5 3.3c-.1.1-.2.1-.3.1zM9 5.4v4.8l3.6-2.4L9 5.4z"></path>
                  <path d="M18.7 15H3c-1.7 0-3-1.3-3-3V3c0-1.7 1.3-3 3-3h15.7c1.7 0 3 1.3 3 3v9c0 1.7-1.3 3-3 3zM3 1c-1.1 0-2 .9-2 2v9c0 1.1.9 2 2 2h15.7c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2H3z"></path>
                </g>
              </svg>
              <span>{i18n.t('video.movieFile')}</span>
              <Meta>
                {formattedDuration}
              </Meta>
            </a>
          </List>
        )}

        {soundSrc && (
          <List>
            <a href={soundSrc} target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21.7 15" id="audio">
                <path
                  fill="currentColor"
                  d="M18.7 15H3c-1.7 0-3-1.3-3-3V3c0-1.7 1.3-3 3-3h15.7c1.7 0 3 1.3 3 3v9c0 1.7-1.3 3-3 3zM3 1c-1.1 0-2 .9-2 2v9c0 1.1.9 2 2 2h15.7c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2H3z"
                ></path>
                <path
                  fill="currentColor"
                  d="M13.7 12c-.1 0-.2 0-.2-.1-.2-.1-.2-.4-.1-.5 3.2-4.2.2-7.4 0-7.5-.1-.1-.1-.4 0-.5.1-.1.4-.1.5 0 0 0 3.6 3.8 0 8.5.1.1 0 .1-.2.1z"
                ></path>
                <path
                  fill="currentColor"
                  d="M12.7 10.6c-.1 0-.2 0-.2-.1-.2-.1-.2-.4-.1-.5 2.1-2.8.1-5 0-5.1-.1-.1-.1-.4 0-.5.1-.1.4-.1.5 0 0 0 2.6 2.7 0 6 .1.1-.1.2-.2.2zM9.7 11l-.2-.2-1.6-1.1c-.4-.3-1.1-.6-1.6-.6h-.4c-.5 0-.9-.3-.9-.8v-.8c0-.5.4-.9.9-.9h.4c.5 0 1.2-.2 1.6-.5l1.7-1.3h.3l.3.2v5.5c0 .3-.2.4-.2.4l-.3.1zM5.9 7.4c-.1 0-.2.1-.2.1v.9s.1.1.2.1h.4c.7 0 1.6.3 2.1.7l1 .8V5.9l-1 .8c-.6.4-1.4.7-2.1.7h-.4zm3.5-2.2z"
                ></path>
              </svg>
              <span>{i18n.t('video.soundFile')}</span>
              <Meta>.{soundSrc.split('.').pop()}</Meta>
            </a>
          </List>
        )}

        {subtitles && (
          <List>
            <a href={subtitles[0].src} target="_blank" rel="noopener noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21.7 15" id="read">
                <g fill="currentColor">
                  <path d="M12.1 11H4.4c-.3 0-.5-.2-.5-.5s.2-.5.5-.5h7.7c.3 0 .5.2.5.5s-.2.5-.5.5zM17.1 8H4.4c-.3 0-.5-.2-.5-.5s.2-.5.5-.5h12.7c.3 0 .5.2.5.5s-.2.5-.5.5zM17.1 5H4.4c-.3 0-.5-.2-.5-.5s.2-.5.5-.5h12.7c.3 0 .5.2.5.5s-.2.5-.5.5z"></path>
                  <path d="M18.7 15H3c-1.7 0-3-1.3-3-3V3c0-1.7 1.3-3 3-3h15.7c1.7 0 3 1.3 3 3v9c0 1.7-1.3 3-3 3zM3 1c-1.1 0-2 .9-2 2v9c0 1.1.9 2 2 2h15.7c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2H3z"></path>
                </g>
              </svg>
              <span>{i18n.t('video.subtitleFile')}</span>
              <Meta>
                {subtitles[0].src.split('.').pop()}, {subtitles[0].subtitleSize}
              </Meta>
            </a>
          </List>
        )}
        <List>
          © 2012-{currentYear} NHG | <a href="/gebruiksvoorwaarden">Disclaimer</a>
        </List>
      </Downloads>
    </>
  )
}

const Header = styled.h2`
  text-align: inherit;
  margin-top: 35px;
  font-size: 20px;

  @media (min-width: ${breakpoints.md}) {
    font-size: 24px;
  }
`

const StyledVideo = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.lightGrey};
  border-radius: 0.24em;
  outline-offset: 2px;

  .video-js {
    border-radius: 0.24em;

    .vjs-big-play-button {
      height: 2.5em;
      width: 2.5em;
      line-height: 2.5em;
      border-radius: 50%;
      background-color: ${rgba(theme.blue, 0.7)};
      border: none;
    }

    li {
      padding-left: inherit;
    }

    li::before {
      content: none;
    }
  }
`

const DownloadHeader = styled.h2`
  margin: 0;
`

const DownloadVideo = styled.button<{ $isOpen: boolean }>`
  color: ${({ theme }) => theme.linkColor};
  background-color: transparent;
  cursor: pointer;
  display: block;
  font-size: 0.9375rem;
  margin: 15px 0 10px;
  position: relative;
  border: 0;
  text-align: left;
  transition: all 0.3s ease;

  & svg {
    width: 12px;
    transition: all 0.3s ease;
    margin-left: 10px;
  }

  &:hover {
    background-color: #eee;
  }

  ${({ $isOpen }) =>
    $isOpen &&
    `
    & svg {
      transform: rotate(180deg);
    }
  `};
`

const Downloads = styled.ul<{ $isOpen: boolean }>`
  background-color: #eee;
  margin: 0;
  max-height: 0;
  visibility: hidden;
  overflow: hidden;
  transition: all 0.3s ease;
  list-style-type: none;

  .text-formatted & {
    padding-left: 0;
  }

  ${({ $isOpen }) =>
    $isOpen &&
    `
    max-height: 360px;
    visibility: visible;
  `};
`

const List = styled.li`
  font-size: 1rem;
  margin: 0;
  padding: 5px 22px;
  text-overflow: ellipsis;
  overflow: hidden;
  text-align: left;

  &:first-child {
    padding-top: 20px;
  }

  &::before {
    visibility: hidden;
  }

  & a {
    border-bottom: 1px solid transparent;
    text-decoration: none;
    color: ${({ theme }) => theme.linkColor};

    &:hover {
      color: ${({ theme }) => theme.textColor};
    }

    &:visited {
      color: #551a8b;
    }

    & svg {
      width: 22px;
      margin-right: 15px;
    }
  }

  &:last-child {
    margin-top: 15px;
    padding-bottom: 20px;

    & a {
      padding-left: 0;
    }
  }

  & span {
    padding-right: 8px;
  }
`

const Meta = styled.span`
  display: inline-block;
  color: ${({ theme }) => theme.lightFontColor};
`

export default Video
